/**
 * MobileBannerRenderer Component
 * Centralized component that renders the appropriate mobile banner
 * Based on the current mobileBannerComponent setting in NavigationContext
 */

import React, { useState, useEffect } from 'react';
import { useNavigation } from '../context/NavigationContext';
import DefaultMobileBanner from './DefaultMobileBanner';
import HomepageMobileBanner from '../homePagePanel/HomepageMobileBanner';
import MobileTopBanner from '../TopChatBanner/mobile/MobileTopBanner';
import { useUsageLimiter } from '../../utils/usageLimiter';
import { useOnboarding } from '../homePagePanel/Onboarding/hooks/useOnboarding';

const MobileBannerRenderer = ({
  user,
  // Props needed for MobileTopBanner (chat)
  userStats,
  responseMode,
  updateUIState,
  RESPONSE_MODES,
  handleNewChat,
  onSuggestionClick,
  onShowMobileStats,
  bets,
  setBets,
  removeBet,
  updateBet,
  handleClearAll,
  handleManualAdd,
  handleExportToClipboard,
  showConfirmClear,
  setShowConfirmClear,
  showAddModal,
  setShowAddModal,
  newPickText,
  setNewPickText,
  deleteTargetId,
  setDeleteTargetId,
  showDeleteConfirm,
  setShowDeleteConfirm,
  handleConfirmDelete,
  gameUsageState,
  runAutomatedGamePrediction,
  currentGameInfo
}) => {
  const { mobileBannerComponent } = useNavigation();
  const [isMobile, setIsMobile] = useState(false);
  const { tier, starCoins } = useUsageLimiter(user?.uid);
  const { shouldShowOnboarding } = useOnboarding(user?.uid);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Don't render on desktop
  if (!isMobile) return null;

  // Fixed positioning wrapper for all banners
  const BannerWrapper = ({ children }) => (
    <div className="fixed top-0 left-0 right-0 z-[45]">
      {children}
    </div>
  );

  // Render the appropriate banner based on mobileBannerComponent
  switch (mobileBannerComponent) {
    case 'none':
      // No banner for this page
      return null;

    case 'homepage':
      // Custom homepage banner with tour functionality
      return (
        <BannerWrapper>
          <HomepageMobileBanner
            user={user}
            tier={tier}
            starCoins={starCoins}
            showOnboarding={shouldShowOnboarding}
            onStartTour={() => {
              window.dispatchEvent(new CustomEvent('startOnboarding'));
            }}
          />
        </BannerWrapper>
      );

    case 'chat':
      // MobileTopBanner for chat interface
      return (
        <BannerWrapper>
          <MobileTopBanner
            userId={user?.uid}
            userStats={userStats}
            responseMode={responseMode}
            updateUIState={updateUIState}
            RESPONSE_MODES={RESPONSE_MODES}
            handleNewChat={handleNewChat}
            onSuggestionClick={onSuggestionClick}
            onShowMobileStats={onShowMobileStats}
            bets={bets}
            setBets={setBets}
            removeBet={removeBet}
            updateBet={updateBet}
            handleClearAll={handleClearAll}
            handleManualAdd={handleManualAdd}
            handleExportToClipboard={handleExportToClipboard}
            showConfirmClear={showConfirmClear}
            setShowConfirmClear={setShowConfirmClear}
            showAddModal={showAddModal}
            setShowAddModal={setShowAddModal}
            newPickText={newPickText}
            setNewPickText={setNewPickText}
            deleteTargetId={deleteTargetId}
            setDeleteTargetId={setDeleteTargetId}
            showDeleteConfirm={showDeleteConfirm}
            setShowDeleteConfirm={setShowDeleteConfirm}
            handleConfirmDelete={handleConfirmDelete}
            tier={tier}
            gameUsageState={gameUsageState}
            runAutomatedGamePrediction={runAutomatedGamePrediction}
            currentGameInfo={currentGameInfo}
          />
        </BannerWrapper>
      );

    case 'default':
    default:
      // Default banner for all other pages
      return (
        <BannerWrapper>
          <DefaultMobileBanner user={user} />
        </BannerWrapper>
      );
  }
};

export default MobileBannerRenderer;